import { defineModule } from '../../engine/module';
import { AssistPanel } from './AssistPanel';

// The AI UI (docs/servicedesk-v2-proposal.md P4). Deliberately small: the copilot lives beside the
// ticket rather than in a chat window of its own, and the rest is administration — how AI is
// reached, what it may do, what it did, and how often people agreed with it. All engine screens.

export { AssistPanel } from './AssistPanel';

export const aiModule = defineModule({
  name: 'ai',
  nav: [
    {
      label: 'Service desk',
      order: 15,
      items: [
        { label: 'AI', to: '/ai-policies', icon: 'sparkles', permission: 'ai-policies:list' },
        { label: 'Agents', to: '/ai-agents', icon: 'sparkles', permission: 'ai-agents:list' },
        { label: 'Knowledge candidates', to: '/knowledge-candidates', icon: 'book-open', permission: 'ai-suggestions:list' },
      ],
    },
  ],
  screens: [
    {
      path: '/ai-policies',
      resource: 'ai-policies',
      kind: 'list',
      title: 'What AI may do',
      subtitle: 'Per class: off, suggest, or act on its own above a confidence you set. Everything starts at suggest.',
      columns: ['action_class', 'mode', 'min_confidence', 'requires_approval'],
      create: false,
      inspector: true,
      actions: [
        { name: 'settings', label: 'Connection', scope: 'collection', placement: 'toolbar', run: ({ navigate }) => navigate('/ai-configs') },
        { name: 'runs', label: 'What it did', scope: 'collection', placement: 'toolbar', run: ({ navigate }) => navigate('/ai-runs') },
        { name: 'suggestions', label: 'Suggestions', scope: 'collection', placement: 'menu', run: ({ navigate }) => navigate('/ai-suggestions') },
      ],
    },
    {
      path: '/ai-configs',
      resource: 'ai-configs',
      kind: 'list',
      title: 'AI connection',
      subtitle: 'Hosted or your own key, the model, the monthly budget — and the switch that stops everything.',
      columns: ['mode', 'model', 'is_enabled', 'api_key_hint', 'monthly_budget_minor'],
      create: false,
      inspector: true,
      hidden: true,
    },
    {
      path: '/ai-agents',
      resource: 'ai-agents',
      kind: 'list',
      title: 'Resolver agents',
      // The subtitle is the warning: this is the only thing here that talks to customers alone.
      subtitle: 'An agent answers customers on its own, inside one published runbook, on one request type. Off until you switch it on.',
      columns: ['name', 'is_enabled', 'ticket_type_id', 'kb_article_id', 'may_resolve', 'min_confidence', 'max_cost_minor', 'client_opt_in_required'],
      filters: [{ field: 'is_enabled', kind: 'chips' }],
      detailPath: '/ai-agents/:id',
      actions: [
        { name: 'runs', label: 'What they did', scope: 'collection', placement: 'toolbar', run: ({ navigate }) => navigate('/ai-agent-runs') },
      ],
    },
    {
      path: '/ai-agents/:id',
      resource: 'ai-agents',
      kind: 'detail',
      builtinTabs: ['overview', 'activity'],
      tabs: [
        { key: 'clients', label: 'Clients opted in', icon: 'building', resource: 'ai-agent-clients', filter: { agent_id: ':id' }, columns: ['client_id', 'is_enabled', 'created_at'] },
        { key: 'runs', label: 'Runs', icon: 'sparkles', resource: 'ai-agent-runs', filter: { agent_id: ':id' }, columns: ['started_at', 'ticket_id', 'state', 'steps', 'cost_minor', 'confidence', 'handoff_reason'] },
      ],
    },
    {
      path: '/ai-agent-runs',
      resource: 'ai-agent-runs',
      kind: 'list',
      title: 'Agent runs',
      subtitle: 'Every ticket an agent worked, what it decided, what it cost — and why it handed back when it did.',
      columns: ['started_at', 'agent_id', 'ticket_id', 'state', 'steps', 'cost_minor', 'confidence', 'handoff_reason'],
      filters: [{ field: 'state', kind: 'chips' }],
      query: { sort: '-started_at' },
      create: false,
      detailPath: '/ai-agent-runs/:id',
      hidden: true,
    },
    {
      path: '/ai-agent-runs/:id',
      resource: 'ai-agent-runs',
      kind: 'detail',
      builtinTabs: ['overview'],
      // The trace is the guardrail made visible: every step, and the model call behind it.
      tabs: [{ key: 'steps', label: 'What it did', icon: 'list-ordered', resource: 'ai-agent-steps', filter: { run_id: ':id' }, columns: ['seq', 'kind', 'ok', 'note', 'ai_run_id', 'ticket_action_id', 'created_at'] }],
      hidden: true,
    },
    {
      path: '/ai-runs',
      resource: 'ai-runs',
      kind: 'list',
      title: 'What the AI did',
      subtitle: 'Every call: what it was for, how sure it was, what it read, what it cost — and every refusal, with its reason.',
      columns: ['created_at', 'action_class', 'status', 'confidence', 'model', 'cost_minor', 'latency_ms', 'error'],
      filters: [{ field: 'status', kind: 'chips' }, { field: 'action_class', kind: 'chips' }],
      query: { sort: '-created_at' },
      create: false,
      inspector: true,
      hidden: true,
    },
    {
      // The knowledge loop: what the answer-quality judge proposed from resolved tickets.
      path: '/knowledge-candidates',
      resource: 'ai-suggestions',
      kind: 'list',
      title: 'Knowledge candidates',
      subtitle: 'Resolved tickets the AI thinks are worth an article, an FAQ or a revision. Drafting only writes a draft — publishing stays yours.',
      columns: ['created_at', 'entity_id', 'confidence', 'state'],
      filters: [{ field: 'state', kind: 'chips' }],
      query: { sort: '-created_at', filter: [{ field: 'kind', op: 'eq', value: 'kb_candidate' }] },
      create: false,
      inspector: true,
    },
    {
      path: '/ai-suggestions',
      resource: 'ai-suggestions',
      kind: 'list',
      title: 'AI suggestions',
      subtitle: 'What it proposed and what people did about it — the number that says whether it is any good.',
      columns: ['created_at', 'kind', 'action_class', 'state', 'confidence', 'applied_by', 'decision_note'],
      filters: [{ field: 'state', kind: 'chips' }],
      query: { sort: '-created_at' },
      create: false,
      inspector: true,
      hidden: true,
    },
  ],
  widgets: { assistPanel: AssistPanel },
  commands: [
    { id: 'ai-agents', label: 'Resolver agents', kind: 'go to', icon: 'sparkles', keywords: 'agent autonomous resolver runbook bot', run: ({ navigate }) => navigate('/ai-agents') },
    { id: 'knowledge-candidates', label: 'Knowledge candidates', kind: 'go to', icon: 'book-open', keywords: 'kb faq article draft judge resolved knowledge loop', run: ({ navigate }) => navigate('/knowledge-candidates') },
    { id: 'ai-policies', label: 'What AI may do', kind: 'go to', icon: 'sparkles', keywords: 'ai policy triage copilot confidence', run: ({ navigate }) => navigate('/ai-policies') },
    { id: 'ai-runs', label: 'What the AI did', kind: 'go to', icon: 'history', keywords: 'ai audit runs cost accuracy', run: ({ navigate }) => navigate('/ai-runs') },
  ],
});
